import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import posthog from "../lib/analytics";
import { Logo } from "../components/Logo";
import { Loader2, Clock, Sparkles, ShieldCheck, MapPin, Heart, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./LandingPageV2.editorial.css";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function LoginScreen() {
  const { t } = useTranslation("landing");
  const { user, loading, refetch } = useAuth();

  const FEATURES = [
    { icon: Clock, label: t("step1_title", "Log shifts in under 3 minutes") },
    { icon: Sparkles, label: t("step2_title", "Auto-generated care summaries") },
    { icon: Receipt, label: t("step3_title", "Auto-invoicing and PDF export") },
    { icon: Heart, label: t("login_feat5_title", "Family portal with care feed") },
    { icon: MapPin, label: t("login_feat6_title", "GPS-verified visits") },
    { icon: ShieldCheck, label: t("login_feat6_privacy", "Your client data stays private") },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSignup = searchParams.get("signup") === "true";
  const isCareComRef = searchParams.get("ref") === "care-com";
  const isDemoHint = searchParams.get("demo") === "true";

  const [email, setEmail] = useState(isDemoHint ? "demo@carelogic.app" : "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [loginMode, setLoginMode] = useState<"otp" | "password">("otp");
  const [step, setStep] = useState<"choose" | "code" | "name">("choose");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan");
    if (params.get("intent") === "upgrade" && (plan === "starter" || plan === "pro")) {
      fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product: plan }),
      })
        .then((r) => r.json())
        .then((d) => { if (d.url) { window.location.href = d.url; } else { navigate("/dashboard", { replace: true }); } })
        .catch(() => navigate("/dashboard", { replace: true }));
    } else {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);
  useEffect(() => { if (step === "code") codeInputRef.current?.focus(); if (step === "name") nameInputRef.current?.focus(); }, [step]);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const redirectAfterLogin = async () => {
    const params = new URLSearchParams(location.search);
    const plan = params.get("plan");
    if (params.get("intent") === "upgrade" && (plan === "starter" || plan === "pro")) {
      try {
        const res = await fetch("/api/stripe/create-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product: plan }),
        });
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
      } catch { /* fall through to dashboard */ }
    }
    try {
      const raw = JSON.parse(localStorage.getItem("carelogic_quick_shifts") || "[]");
      if (Array.isArray(raw) && raw.some((s: { endedAt: string | null }) => s.endedAt)) {
        navigate("/log/import");
        return;
      }
    } catch { /* fall through to dashboard */ }
    navigate("/dashboard");
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: "#5B667D", fontSize: 14, background: "#F5EFE6" }}>
      Loading...
    </div>
  );

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSendCode = async () => {
    if (!isValidEmail(email)) { setError("Please enter a valid email address"); return; }
    setSending(true); setError("");
    try {
      const res = await fetch("/api/auth/request-otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      if (res.status === 429) { setError("Too many attempts. Please wait a few minutes."); return; }
      if (!res.ok) { setError("Couldn't send code. Try again."); return; }
      setStep("code");
      setResendCooldown(30);
    } catch { setError("Couldn't send code. Try again."); }
    finally { setSending(false); }
  };

  const handlePasswordLogin = async () => {
    if (!isValidEmail(email)) { setError("Please enter a valid email address"); return; }
    if (!password || password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setVerifying(true); setError("");
    try {
      const res = await fetch("/api/auth/login-password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (res.status === 429) { setError("Too many attempts. Try again in 15 minutes."); setVerifying(false); return; }
      if (!res.ok) { const data = await res.json(); setError(data.error || "Invalid email or password"); setVerifying(false); return; }
      posthog.capture("user_logged_in", { method: "password" });
      await refetch();
      redirectAfterLogin();
    } catch { setError("Login failed. Try again."); }
    finally { setVerifying(false); }
  };

  const handleVerify = async (codeVal: string) => {
    if (codeVal.length !== 6) return;
    setVerifying(true); setError("");
    try {
      const referralSource = localStorage.getItem("carelogic_ref") ?? undefined;
      let timezone: string | undefined;
      try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* older browsers */ }
      const res = await fetch("/api/auth/verify-otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, code: codeVal, referralSource, timezone }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Invalid code"); setVerifying(false); return; }
      localStorage.removeItem("carelogic_ref");
      posthog.capture("user_logged_in");
      if (!data.user?.first_name) {
        setStep("name");
        setVerifying(false);
        return;
      }
      refetch();
      await redirectAfterLogin();
    } catch { setError("Verification failed. Try again."); setVerifying(false); }
  };

  const handleCodeChange = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 6);
    setCode(cleaned);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setSending(true); setError("");
    try {
      await fetch("/api/auth/request-otp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      setResendCooldown(30);
      setCode("");
    } catch { setError("Couldn't resend. Try again."); }
    finally { setSending(false); }
  };

  const handleCompleteSignup = async () => {
    setSavingName(true); setError("");
    try {
      if (firstName.trim()) {
        await fetch("/api/auth/profile", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ first_name: firstName.trim() }) });
      }
      if (signupPassword.length >= 8) {
        await fetch("/api/auth/set-password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: signupPassword }) });
      }
      await refetch();
      posthog.capture("signup_completed", { ref: localStorage.getItem("carelogic_ref") ?? null });
      navigate("/onboarding");
    } catch { setError("Something went wrong"); }
    finally { setSavingName(false); }
  };

  const inTaxWindow = (() => {
    const now = new Date();
    const m = now.getMonth();
    const d = now.getDate();
    return m === 2 || (m === 3 && d <= 14);
  })();

  return (
    <div className="landing-premium lp-auth">
      {step === "code" && (
        <button
          className="lp-auth-back"
          onClick={() => { setStep("choose"); setCode(""); setError(""); }}
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      )}

      <div className="lp-auth-grid">
        {/* Left — form column */}
        <div className="lp-auth-form-col">
          <div className="lp-auth-form-inner">
            <a href="/" className="lp-auth-logo" aria-label="CareLogic">
              <Logo size="lg" />
            </a>

            {isCareComRef && step === "choose" && (
              <div className="lp-auth-welcome">— Welcome from Care.com</div>
            )}

            <h1 className="lp-auth-title">
              {step === "choose" ? (
                isSignup ? (
                  <>Create your <em>free account.</em></>
                ) : (
                  <>Welcome back <em>to CareLogic.</em></>
                )
              ) : step === "code" ? (
                <>Check your email <em>for a code.</em></>
              ) : (
                <>One last thing <em>before we go.</em></>
              )}
            </h1>

            {step === "choose" && (
              <p className="lp-auth-sub">
                {t("login_value_prop", "Log shifts. Send invoices. Show families the care you deliver.")}
              </p>
            )}
            {step === "code" && (
              <p className="lp-auth-sub">
                Sent to <strong style={{ color: "#14243F", fontWeight: 600 }}>{email}</strong>
              </p>
            )}
            {step === "name" && (
              <p className="lp-auth-sub">
                Tell us your name and set a password if you'd like to skip the email code next time.
              </p>
            )}

            {step === "choose" && inTaxWindow && (
              <div className="lp-auth-tax-bar">
                {t("tax_bar", "Track your shifts, log mileage, and see your deduction estimate before April 15. Free to start.")}
              </div>
            )}

            {error && <div className="lp-auth-error" role="alert">{error}</div>}

            {step === "choose" ? (
              <>
                <button
                  className="lp-auth-google"
                  onClick={() => {
                    setGoogleLoading(true);
                    const ref = localStorage.getItem("carelogic_ref");
                    window.location.href = ref ? `/api/auth/google?ref=${encodeURIComponent(ref)}` : "/api/auth/google";
                  }}
                  disabled={googleLoading}
                >
                  <GoogleIcon />
                  {googleLoading ? t("login_redirecting", "Redirecting...") : t("cta_google", "Continue with Google")}
                </button>

                <div className="lp-auth-divider">or</div>

                {isDemoHint && (
                  <div style={{
                    background: "var(--color-primary-light, #EEF0FF)",
                    border: "1px solid var(--color-primary, #7B6DAF)",
                    borderRadius: 8, padding: "10px 14px",
                    fontSize: 13, marginBottom: 12,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Demo account</div>
                    <div>Email: <code>demo@carelogic.app</code></div>
                    <div>Password: <code>demo2026</code></div>
                  </div>
                )}

                <div className="lp-auth-field">
                  <label className="lp-auth-label" htmlFor="email">{t("login_email_label", "Email")}</label>
                  <input
                    id="email"
                    className="lp-auth-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && (loginMode === "password" ? handlePasswordLogin() : handleSendCode())}
                    autoFocus
                    autoComplete="email"
                  />
                </div>

                {loginMode === "password" && (
                  <div className="lp-auth-field">
                    <label className="lp-auth-label" htmlFor="password">Password</label>
                    <input
                      id="password"
                      ref={passwordInputRef}
                      className="lp-auth-input"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
                      autoComplete="current-password"
                    />
                  </div>
                )}

                <button
                  className="lp-auth-btn"
                  onClick={loginMode === "password" ? handlePasswordLogin : handleSendCode}
                  disabled={sending || verifying || !isValidEmail(email) || (loginMode === "password" && password.length < 8)}
                >
                  {(sending || verifying) ? <Loader2 size={18} className="lp-spin" /> : loginMode === "password" ? "Sign in" : t("login_continue", "Continue")}
                </button>

                <div className="lp-auth-links">
                  <button onClick={() => { setLoginMode(loginMode === "otp" ? "password" : "otp"); setError(""); setPassword(""); }}>
                    {loginMode === "otp" ? "Use password" : "Use email code"}
                  </button>
                  {loginMode === "password" && (
                    <button onClick={() => { setLoginMode("otp"); setError(""); setPassword(""); handleSendCode(); }}>
                      Forgot password?
                    </button>
                  )}
                </div>

                <p className="lp-auth-fineprint">
                  {t("login_trust_signal", "Start free — no credit card. Pro plan available.")}
                </p>
                <p className="lp-auth-legal">
                  <a href="/privacy">{t("login_privacy", "Privacy")}</a>
                  ·
                  <a href="/terms">{t("login_terms", "Terms")}</a>
                </p>
              </>
            ) : step === "code" ? (
              <>
                <div className="lp-auth-field">
                  <label className="lp-auth-label" htmlFor="code">{t("login_enter_code", "Enter code")}</label>
                  <input
                    id="code"
                    ref={codeInputRef}
                    className="lp-auth-input lp-auth-input-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && code.length === 6 && handleVerify(code)}
                  />
                </div>

                <div className="lp-auth-resend">
                  {t("login_didnt_get_it", "Didn't get it?")}{" "}
                  {resendCooldown > 0 ? (
                    <span>{t("login_resend_in", "Resend in {{seconds}}s", { seconds: resendCooldown })}</span>
                  ) : (
                    <button onClick={handleResend} disabled={sending}>
                      {t("login_send_new_code", "Send new code")}
                    </button>
                  )}
                </div>

                <button
                  className="lp-auth-btn"
                  onClick={() => handleVerify(code)}
                  disabled={verifying || code.length !== 6}
                >
                  {verifying ? <Loader2 size={18} className="lp-spin" /> : t("login_sign_in", "Sign in")}
                </button>

                <div className="lp-auth-links">
                  <button onClick={() => { setStep("choose"); setCode(""); setError(""); }}>
                    {t("login_use_different_email", "Use a different email")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="lp-auth-field">
                  <label className="lp-auth-label" htmlFor="firstName">{t("login_name_label", "What should we call you?")}</label>
                  <input
                    id="firstName"
                    ref={nameInputRef}
                    className="lp-auth-input"
                    type="text"
                    placeholder={t("login_name_placeholder", "Your first name")}
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && nameInputRef.current?.blur()}
                    autoComplete="given-name"
                  />
                </div>

                <div className="lp-auth-field">
                  <label className="lp-auth-label" htmlFor="signupPassword">Set a password (optional)</label>
                  <input
                    id="signupPassword"
                    className="lp-auth-input"
                    type="password"
                    placeholder="At least 8 characters"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <div className="lp-auth-hint">
                    You can always sign in with an email code. A password lets you skip the code.
                  </div>
                </div>

                <button
                  className="lp-auth-btn"
                  onClick={handleCompleteSignup}
                  disabled={savingName || !firstName.trim() || (signupPassword.length > 0 && signupPassword.length < 8)}
                >
                  {savingName ? <Loader2 size={18} className="lp-spin" /> : t("login_continue", "Continue")}
                </button>

                {signupPassword.length > 0 && signupPassword.length < 8 && (
                  <div className="lp-auth-hint lp-auth-hint-error">Password must be at least 8 characters</div>
                )}

                <div className="lp-auth-links">
                  <button onClick={() => { refetch(); navigate("/onboarding"); }}>
                    {t("login_skip", "Skip for now")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right — editorial promo */}
        <aside className="lp-auth-promo">
          <div className="lp-auth-promo-inner">
            <div className="lp-auth-promo-eyebrow">— What you get</div>
            <h2 className="lp-auth-promo-title">
              Everything you need to <em>run your practice.</em>
            </h2>

            <ul className="lp-auth-features">
              {FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <li key={f.label}>
                    <Icon size={18} strokeWidth={1.75} />
                    {f.label}
                  </li>
                );
              })}
            </ul>

            <div className="lp-auth-promo-quote">
              Log a shift, generate a care summary, and send the invoice — in under three minutes.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
